import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Eraser, Heading2, Heading3, Italic, List, ListOrdered } from "lucide-react";
import { useEffect } from "react";

interface FeedbackNotesEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  ariaLabel?: string;
}

export function isEmptyNotesHtml(html: string): boolean {
  return (
    html
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, "")
      .trim().length === 0
  );
}

export function FeedbackNotesEditor({
  value,
  onChange,
  placeholder = "Interview notes...",
  ariaLabel = "Interview notes",
}: FeedbackNotesEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value || "",
    immediatelyRender: false,
    editorProps: {
      attributes: {
        "aria-label": ariaLabel,
        class:
          "prose prose-sm dark:prose-invert max-w-none min-h-[80px] px-3 py-2 text-xs text-text-primary focus:outline-none",
      },
    },
    onUpdate({ editor: ed }) {
      const html = ed.getHTML();
      onChange(isEmptyNotesHtml(html) ? "" : html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (isEmptyNotesHtml(current) && isEmptyNotesHtml(incoming)) return;
    if (current === incoming) return;
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [editor, value]);

  if (!editor) return null;

  const isEmpty = isEmptyNotesHtml(editor.getHTML());

  return (
    <div className="rounded border border-input-border bg-card">
      <div className="flex flex-wrap items-center gap-1 border-b border-border p-1">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Bold"
        >
          <Bold size={12} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Italic"
        >
          <Italic size={12} />
        </ToolbarButton>
        <div className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
          title="Heading 2"
        >
          <Heading2 size={12} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}
          title="Heading 3"
        >
          <Heading3 size={12} />
        </ToolbarButton>
        <div className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Bullet list"
        >
          <List size={12} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          title="Ordered list"
        >
          <ListOrdered size={12} />
        </ToolbarButton>
        <div className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          title="Clear formatting"
        >
          <Eraser size={12} />
        </ToolbarButton>
      </div>
      <div className="relative">
        <EditorContent editor={editor} />
        {isEmpty && (
          <div
            aria-hidden
            className="pointer-events-none absolute left-3 top-2 select-none text-xs text-text-muted"
          >
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolbarButton({
  children,
  onClick,
  active,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`rounded p-1 ${
        active ? "bg-accent/10 text-accent" : "text-text-secondary hover:bg-card-hover"
      }`}
    >
      {children}
    </button>
  );
}
